import React, { useEffect, useRef, useState } from "react";
import { Form, Input, Button, Typography, Checkbox, App } from "antd";
import { SubjectEntityInput } from "../../../services/subjects/subjects.types";
import { useLocation, useNavigate } from "react-router-dom";
import "./subjectForm.scss";
import {
  createSubject,
  getSubjectById,
  updateSubject,
} from "../../../services/subjects/subjects.service";
import ActivityIndicator from "../../ActivityIndicator";
import { useDispatch } from "react-redux";
import { handleSubmitFormResponseManage } from "../../../utils";

const { Title } = Typography;

const SubjectForm: React.FC = () => {
  const { notification } = App.useApp();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const subjectId = location?.state as number;
  // Synchronous re-entrancy guard: `loading` lags a render behind, so a fast
  // double click on Guardar would fire two create/update calls.
  const submitInFlightRef = useRef(false);

  const fetchInfo = async () => {
    setLoading(true);
    try {
      if (subjectId) {
        const response = await getSubjectById(subjectId);
        // Services are being migrated to throw on error instead of
        // returning null — treat a falsy result as a failed load either way.
        if (!response) {
          throw new Error("No se pudo cargar la asignatura.");
        }
        form.setFieldsValue({
          code: response.code,
          name: response.name,
          hidden: response.hidden,
        });
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setLoadFailed(true);
      console.error(error);
      const backendMessage = error?.response?.data?.message;
      notification.error({
        message: "Error",
        description: backendMessage
          ? `No se pudo cargar la información. Reintentá o volvé al listado.\n${backendMessage}`
          : "No se pudo cargar la información. Reintentá o volvé al listado.",
        placement: "top",
      });
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  const handleSubmit = async (values: SubjectEntityInput) => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      await submitSubject(values);
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const submitSubject = async (values: SubjectEntityInput) => {
    setLoading(true);
    try {
      await handleSubmitFormResponseManage(
        values,
        createSubject,
        updateSubject,
        navigate,
        dispatch,
        "subject",
        "addSubject",
        "updateSubject",
        subjectId
      );
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notification.error({
        message: "Error",
        description: "Ocurrió un error al guardar la asignatura.",
        placement: "top",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <div className="subjectFormContainer">
      <Title level={3}>Detalles de la Asignatura</Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="subjectForm"
      >
        <Form.Item
          label="Código"
          name="code"
          rules={[{ required: true, message: "El código es requerido" }]}
        >
          <Input placeholder="Ej. MAT101" />
        </Form.Item>

        <Form.Item
          label="Nombre"
          name="name"
          rules={[{ required: true, message: "El nombre es requerido" }]}
        >
          <Input placeholder="Ej. Matemáticas" />
        </Form.Item>

        <Form.Item
          label="Oculto"
          name="hidden"
          valuePropName="checked"
          initialValue={false}
        >
          <Checkbox>Marcar como oculto</Checkbox>
        </Form.Item>

        <Form.Item>
          <div className="formButtons">
            <Button htmlType="button" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" disabled={loadFailed}>
              Guardar asignatura
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default SubjectForm;
